import { ReactNode } from 'react';
import LoadingFallback, { LoadingVariant } from './LoadingFallback';
import EmptyState, { EmptyStateType } from './EmptyState';
import ApiErrorState, { ErrorType, getErrorTypeFromError } from './ApiErrorState';
import { cn } from '@/lib/utils';

interface DataFetcherProps<T> {
  // Data state
  data: T | null | undefined;
  isLoading: boolean;
  error?: Error | null;

  // Rendering
  children: (data: T) => ReactNode;

  // Loading config
  loadingVariant?: LoadingVariant;
  loadingMessage?: string;
  loadingCount?: number;
  loadingColumns?: number;

  // Error config
  errorType?: ErrorType;
  errorTitle?: string;
  errorDescription?: string;
  onRetry?: () => void;
  showContactOnError?: boolean;

  // Empty state config
  emptyType?: EmptyStateType;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };

  // Check for empty - can be customized
  isEmpty?: (data: T) => boolean;

  // Styling
  className?: string;
  loadingClassName?: string;
  errorClassName?: string;
  emptyClassName?: string;
}

// Default empty check for arrays and objects
const defaultIsEmpty = <T,>(data: T): boolean => {
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object' && data !== null) {
    return Object.keys(data).length === 0;
  }
  return !data;
};

function DataFetcher<T>({
  data,
  isLoading,
  error,
  children,
  loadingVariant = 'spinner',
  loadingMessage,
  loadingCount = 4,
  loadingColumns = 3,
  errorType,
  errorTitle,
  errorDescription,
  onRetry,
  showContactOnError = false,
  emptyType = 'generic',
  emptyTitle,
  emptyDescription,
  emptyAction,
  isEmpty = defaultIsEmpty,
  className,
  loadingClassName,
  errorClassName,
  emptyClassName,
}: DataFetcherProps<T>) {
  // Loading state
  if (isLoading) {
    return (
      <div className={cn(className, loadingClassName)}>
        <LoadingFallback
          variant={loadingVariant}
          message={loadingMessage}
          count={loadingCount}
          columns={loadingColumns}
        />
      </div>
    );
  }

  // Error state
  if (error) {
    const derivedErrorType = errorType || getErrorTypeFromError(error);
    return (
      <div className={cn(className, errorClassName)}>
        <ApiErrorState
          type={derivedErrorType}
          title={errorTitle}
          description={errorDescription}
          error={error}
          onRetry={onRetry}
          showContactButton={showContactOnError}
        />
      </div>
    );
  }

  // Empty state
  if (!data || isEmpty(data as T)) {
    return (
      <div className={cn(className, emptyClassName)}>
        <EmptyState
          type={emptyType}
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      </div>
    );
  }

  // Render children with data
  return <>{children(data as T)}</>;
}

export default DataFetcher;

// Convenient hook for data fetching with fallback states
export interface UseDataFetcherResult<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// Example usage:
// <DataFetcher
//   data={products}
//   isLoading={loading}
//   error={error}
//   loadingVariant="grid"
//   loadingCount={8}
//   emptyType="products"
//   onRetry={refetch}
// >
//   {(products) => (
//     <div className="grid grid-cols-3 gap-4">
//       {products.map(p => <ProductCard key={p.id} product={p} />)}
//     </div>
//   )}
// </DataFetcher>
